import { useEffect, useState } from "react";
import {
  ApiError,
  getMonthlyConfig,
  getMonthlyDetail,
  getMonthlyLeaderboard,
  getRepriceStatus,
  listSavedFilters,
  runStrategySim,
  setMonthlyConfig,
  WIN_RATE_THRESHOLDS,
  type MonthlyConfigDto,
  type MonthlyDetailResponse,
  type MonthlyLeaderboardRow,
  type SavedFilterDto,
  type StrategySimDayResult,
  type StrategySimResponse,
  type WinRateStat,
} from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";

function pct(v: number | null): string {
  return v === null ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(2)}%`;
}

function PctSpan({ v }: { v: number | null }) {
  if (v === null) return <span className="mut">—</span>;
  return <span className={`num ${v >= 0 ? "pos" : "neg"}`}>{pct(v)}</span>;
}

/** The threshold dropdown shared by both win-rate columns -- "probability
 * of a day's return >= X%", 0 = plain positive-return probability. */
function ThresholdSelect({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <select
      value={value}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ fontSize: "0.7rem", marginTop: 2, fontWeight: 400 }}
    >
      {WIN_RATE_THRESHOLDS.map((t) => (
        <option key={t} value={t}>
          ≥ {t}%
        </option>
      ))}
    </select>
  );
}

function WinRateSpan({ stat }: { stat: WinRateStat | undefined }) {
  if (!stat || stat.probability === null) return <span className="mut">—</span>;
  return (
    <span className="num" title={`${stat.count} of ${stat.total} days`}>
      {(stat.probability * 100).toFixed(0)}%
      <span className="mut" style={{ fontSize: "0.72rem" }}> ({stat.count}/{stat.total})</span>
    </span>
  );
}

/**
 * Strategy Backtest Simulator (2026-09-25) -- re-runs a saved Stocks-page
 * strategy across the last M trading days: top-N by signal, held H days,
 * scored with the exact same Realized/Max Realization convention (including
 * the intraday-high fix) the Stocks page's own backtest columns use.
 *
 * Deliberately NOT the Simulation Workbench (that's a single-stock forward
 * Monte Carlo payoff distribution, reached only via "Simulate this ->" deep
 * links) -- this is a retrospective, mechanical rule test over real history.
 */
export function StrategySimulatorPage({
  token,
  onUnauthorized,
}: {
  token: string;
  onUnauthorized: () => void;
}) {
  const [savedFilters, setSavedFilters] = useState<SavedFilterDto[]>([]);
  const [filterId, setFilterId] = useState<string>("");
  const [topN, setTopN] = useState(5);
  const [horizonDays, setHorizonDays] = useState(10);
  const [lookbackDays, setLookbackDays] = useState(30);
  const [sortBy, setSortBy] = useState<"signal_equal" | "signal_skill_weighted">("signal_equal");
  const [result, setResult] = useState<StrategySimResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);

  // Monthly leaderboard -- read-only, permanently cached by a monthly cron
  // (app/factory/monthly_strategy_sim.py). This page never triggers a
  // computation; it only displays what the cron has already produced.
  const [monthlyConfig, setMonthlyConfigState] = useState<MonthlyConfigDto | null>(null);
  const [monthlyMonth, setMonthlyMonth] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [monthlyRows, setMonthlyRows] = useState<MonthlyLeaderboardRow[] | null>(null);
  const [monthlyError, setMonthlyError] = useState<string | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configJustSaved, setConfigJustSaved] = useState(false);
  // Win-rate probability thresholds -- one shared dropdown per column
  // (Realized / Max Realization), applied to every row in the leaderboard.
  const [realizedThreshold, setRealizedThreshold] = useState(0);
  const [maxRealizedThreshold, setMaxRealizedThreshold] = useState(0);
  // Same idea, separate state, for the one-off tool's own result below --
  // a different view of a different (possibly differently-scoped) run.
  const [oneOffRealizedThreshold, setOneOffRealizedThreshold] = useState(0);
  const [oneOffMaxRealizedThreshold, setOneOffMaxRealizedThreshold] = useState(0);

  function handleAuthAwareError(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      onUnauthorized();
      return true;
    }
    return false;
  }

  useEffect(() => {
    listSavedFilters(token)
      .then((rows) => {
        setSavedFilters(rows);
        if (rows.length > 0) setFilterId(rows[0].filter_id);
      })
      .catch((err) => {
        if (!handleAuthAwareError(err)) console.error(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    getMonthlyConfig(token)
      .then(setMonthlyConfigState)
      .catch((err) => {
        if (!handleAuthAwareError(err)) console.error(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    getMonthlyLeaderboard(token, monthlyMonth)
      .then((rows) => {
        setMonthlyRows(rows);
        setMonthlyError(null);
      })
      .catch((err) => {
        if (handleAuthAwareError(err)) return;
        setMonthlyError(err instanceof ApiError ? `${err.status}: ${err.message}` : "failed to load leaderboard");
        setMonthlyRows(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, monthlyMonth]);

  async function saveMonthlyConfig(next: MonthlyConfigDto) {
    setSavingConfig(true);
    setConfigJustSaved(true);
    try {
      const saved = await setMonthlyConfig(token, next);
      setMonthlyConfigState(saved);
      setSavingConfig(false);
      // Repricing runs as a BACKGROUND JOB (see backend comment on this
      // endpoint) -- usually near-instant for months already fully cached,
      // but a month computed before candidate-caching existed needs a real
      // one-time backfill first. Poll the job itself rather than guessing a
      // delay: this is also what LOCKS the inputs (via configJustSaved)
      // until it's actually safe to change the setting again -- firing
      // another change before this one lands is exactly what produced a
      // real, confusing inconsistency live (one strategy stuck on an old
      // value while another had already updated).
      for (;;) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const status = await getRepriceStatus(token, saved.job_id);
          if (status.status !== "running") break;
        } catch {
          break; // job status unavailable -- don't lock the UI forever over it
        }
      }
      getMonthlyLeaderboard(token, monthlyMonth).then(setMonthlyRows).catch(() => {});
    } catch (err) {
      if (!handleAuthAwareError(err)) console.error(err);
    } finally {
      setSavingConfig(false);
      setConfigJustSaved(false);
    }
  }

  async function run() {
    if (!filterId) {
      setError("save a strategy on the Stocks page first, then pick it here");
      return;
    }
    setLoading(true);
    setError(null);
    setExpandedDate(null);
    try {
      const res = await runStrategySim(token, filterId, topN, horizonDays, lookbackDays, sortBy);
      setResult(res);
    } catch (err) {
      if (handleAuthAwareError(err)) return;
      setError(err instanceof ApiError ? `${err.status}: ${err.message}` : "simulation failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  function exportCsv() {
    if (!result) return;
    const rows = result.days.map((d) => ({
      entry_date: d.entry_date,
      exit_date: d.exit_date,
      forced_early_exit: d.forced_early_exit,
      avg_realized: d.avg_realized,
      avg_max_realized: d.avg_max_realized,
      n_picks: d.picks.length,
    }));
    exportRowsAsCsv(
      rows,
      [
        { key: "entry_date", label: "Entry date" },
        { key: "exit_date", label: "Exit date" },
        { key: "forced_early_exit", label: "Forced early exit" },
        { key: "avg_realized", label: "Avg realized" },
        { key: "avg_max_realized", label: "Avg max realized" },
        { key: "n_picks", label: "Picks" },
      ],
      `strategy_sim_${result.strategy_name}.csv`,
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Simulator</h1>
          <div className="page-sub">
            Strategy backtest — re-run a saved strategy over real history <FieldHelp term="strategy_sim" />
          </div>
        </div>
      </div>

      <PageHelper>
        Pick a strategy you saved on the Stocks page, and see how a mechanical rule — "every day, buy the top N by
        signal, sell after H days" — would actually have performed over the last M trading days. Not a
        recommendation: it measures what a rule would have done, nothing more. Entry is always the entry date's
        CLOSING price (the same convention every model here is trained and scored on); Realized exits at the
        horizon day's close, Max Realization at the best intraday high reached along the way.
      </PageHelper>

      <section className="results-panel" style={{ marginBottom: 20 }}>
        <div className="page-sub" style={{ padding: "12px 14px 0" }}>
          <b>Monthly leaderboard</b> — pre-computed by a monthly cron, not run live on this page{" "}
          <FieldHelp term="strategy_sim" />
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end", padding: 14 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="mut" style={{ fontSize: "0.75rem" }}>Month</span>
            <input
              type="month"
              value={monthlyMonth.slice(0, 7)}
              onChange={(e) => setMonthlyMonth(`${e.target.value}-01`)}
            />
          </label>
          {monthlyConfig && (
            <>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span className="mut" style={{ fontSize: "0.75rem" }}>Top N (global, all strategies)</span>
                <input
                  type="number" min={1} max={50} style={{ width: 70 }}
                  value={monthlyConfig.top_n}
                  disabled={configJustSaved}
                  onChange={(e) => setMonthlyConfigState({ ...monthlyConfig, top_n: Number(e.target.value) })}
                  onBlur={() => saveMonthlyConfig(monthlyConfig)}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span className="mut" style={{ fontSize: "0.75rem" }}>Horizon H (global)</span>
                <input
                  type="number" min={1} max={252} style={{ width: 90 }}
                  value={monthlyConfig.horizon_days}
                  disabled={configJustSaved}
                  onChange={(e) => setMonthlyConfigState({ ...monthlyConfig, horizon_days: Number(e.target.value) })}
                  onBlur={() => saveMonthlyConfig(monthlyConfig)}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span className="mut" style={{ fontSize: "0.75rem" }}>Sort by (global)</span>
                <select
                  value={monthlyConfig.sort_by}
                  disabled={configJustSaved}
                  onChange={(e) => saveMonthlyConfig({ ...monthlyConfig, sort_by: e.target.value as MonthlyConfigDto["sort_by"] })}
                >
                  <option value="signal_equal">Signal (equal-weight)</option>
                  <option value="signal_skill_weighted">Signal · skill (shrunk IC-weight)</option>
                </select>
              </label>
              {savingConfig && <span className="mut" style={{ fontSize: "0.75rem" }}>saving…</span>}
              {configJustSaved && !savingConfig && (
                <span className="mut" style={{ fontSize: "0.75rem" }} title="Recomputing every cached month under the new setting -- inputs are locked until this finishes, so another change can't race with it and leave one strategy on a stale value (a real bug this fixed).">
                  ⏳ recomputing — inputs locked until this finishes…
                </span>
              )}
            </>
          )}
        </div>

        {monthlyError && <div className="error" style={{ margin: 14 }}>{monthlyError}</div>}

        {monthlyRows && monthlyRows.length === 0 && (
          <div className="empty-state" style={{ margin: 14 }}>
            No cached results for this month yet — the monthly cron runs on the 11th of each month. A brand-new
            saved strategy won't have a row until the next cron run picks it up.
          </div>
        )}

        {monthlyRows && monthlyRows.length > 0 && (
          <div className="table-wrap">
            <div className="mut" style={{ fontSize: "0.75rem", padding: "0 14px 6px" }}>
              Click a strategy to see which stocks were picked each day.
            </div>
            <table className="stocks-table">
              <thead>
                <tr>
                  <th className="l"></th>
                  <th className="l">Strategy</th>
                  <th className="c">Avg realized</th>
                  <th className="c">Avg max realization</th>
                  <th className="c">
                    <div>Prob. realized</div>
                    <ThresholdSelect value={realizedThreshold} onChange={setRealizedThreshold} />
                  </th>
                  <th className="c">
                    <div>Prob. max realization</div>
                    <ThresholdSelect value={maxRealizedThreshold} onChange={setMaxRealizedThreshold} />
                  </th>
                  <th className="c">Best day</th>
                  <th className="c">Worst day</th>
                  <th className="l">N / H</th>
                  <th className="l">Days</th>
                  <th className="l">Status</th>
                </tr>
              </thead>
              <tbody>
                {monthlyRows.map((r, i) => (
                  <MonthlyRowExpandable
                    key={r.filter_id}
                    row={r}
                    isWinner={i === 0}
                    token={token}
                    onAuthError={handleAuthAwareError}
                    realizedThreshold={realizedThreshold}
                    maxRealizedThreshold={maxRealizedThreshold}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="results-panel">
        <div className="page-sub" style={{ padding: "12px 14px 0" }}>
          <b>One-off simulation</b> — runs live when you click, small M only (large M/H can exceed the request timeout;
          use the monthly leaderboard above for a real ongoing track record)
        </div>
        <div className="filter-head" style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end", padding: 14 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="mut" style={{ fontSize: "0.75rem" }}>Strategy</span>
            <select value={filterId} onChange={(e) => setFilterId(e.target.value)}>
              {savedFilters.length === 0 && <option value="">no saved strategies yet</option>}
              {savedFilters.map((f) => (
                <option key={f.filter_id} value={f.filter_id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="mut" style={{ fontSize: "0.75rem" }}>Top N stocks</span>
            <input type="number" min={1} max={50} value={topN} onChange={(e) => setTopN(Number(e.target.value))} style={{ width: 70 }} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="mut" style={{ fontSize: "0.75rem" }}>Horizon (H, trading days)</span>
            <input type="number" min={1} max={252} value={horizonDays} onChange={(e) => setHorizonDays(Number(e.target.value))} style={{ width: 90 }} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="mut" style={{ fontSize: "0.75rem" }}>Trading days to simulate (M)</span>
            <input type="number" min={1} max={200} value={lookbackDays} onChange={(e) => setLookbackDays(Number(e.target.value))} style={{ width: 90 }} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="mut" style={{ fontSize: "0.75rem" }}>Sort by</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as "signal_equal" | "signal_skill_weighted")}>
              <option value="signal_equal">Signal (equal-weight)</option>
              <option value="signal_skill_weighted">Signal · skill (shrunk IC-weight)</option>
            </select>
          </label>
          <button className="btn primary" onClick={run} disabled={loading || !filterId}>
            {loading ? "Running…" : "Run simulation"}
          </button>
        </div>

        {error && <div className="error" style={{ margin: 14 }}>{error}</div>}

        {result && (
          <>
            <div className="foot-note" style={{ padding: "8px 14px" }}>
              <b>{result.strategy_name}</b> · top {result.top_n} · {result.horizon_days}d horizon ·{" "}
              {result.summary.days_run} of {result.lookback_days} entry days scored
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", padding: "0 14px 14px" }}>
              <SummaryCard
                label="Realized"
                avg={result.summary.avg_realized}
                bestDate={result.summary.best_realized_date}
                bestValue={result.summary.best_realized_value}
                worstDate={result.summary.worst_realized_date}
                worstValue={result.summary.worst_realized_value}
                winRates={result.summary.realized_win_rates}
                threshold={oneOffRealizedThreshold}
                onThresholdChange={setOneOffRealizedThreshold}
              />
              <SummaryCard
                label="Max Realization"
                avg={result.summary.avg_max_realized}
                bestDate={result.summary.best_max_realized_date}
                bestValue={result.summary.best_max_realized_value}
                worstDate={result.summary.worst_max_realized_date}
                worstValue={result.summary.worst_max_realized_value}
                winRates={result.summary.max_realized_win_rates}
                threshold={oneOffMaxRealizedThreshold}
                onThresholdChange={setOneOffMaxRealizedThreshold}
              />
            </div>

            <div className="table-toolbar" style={{ padding: "0 14px 10px", display: "flex", justifyContent: "flex-end" }}>
              <button className="btn ghost sm" onClick={exportCsv}>↓ CSV</button>
            </div>

            <div className="table-wrap">
              <table className="stocks-table">
                <thead>
                  <tr>
                    <th className="l">Entry date</th>
                    <th className="l">Exit date</th>
                    <th className="c">Avg realized</th>
                    <th className="c">Avg max realization</th>
                    <th className="l">Picks</th>
                  </tr>
                </thead>
                <tbody>
                  {result.days.map((d) => (
                    <DayRow
                      key={d.entry_date}
                      day={d}
                      expanded={expandedDate === d.entry_date}
                      onToggle={() => setExpandedDate(expandedDate === d.entry_date ? null : d.entry_date)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </>
  );
}

function SummaryCard({
  label,
  avg,
  bestDate,
  bestValue,
  worstDate,
  worstValue,
  winRates,
  threshold,
  onThresholdChange,
}: {
  label: string;
  avg: number | null;
  bestDate: string | null;
  bestValue: number | null;
  worstDate: string | null;
  worstValue: number | null;
  winRates: Record<string, WinRateStat>;
  threshold: number;
  onThresholdChange: (v: number) => void;
}) {
  return (
    <div className="card" style={{ padding: 12, minWidth: 240 }}>
      <div className="mut" style={{ fontSize: "0.75rem", marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div>
          Average <PctSpan v={avg} />
        </div>
        <div className="mut" style={{ fontSize: "0.8rem" }}>
          Best: {bestDate ?? "—"} <PctSpan v={bestValue} />
        </div>
        <div className="mut" style={{ fontSize: "0.8rem" }}>
          Worst: {worstDate ?? "—"} <PctSpan v={worstValue} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
          <span className="mut" style={{ fontSize: "0.8rem" }}>Prob.</span>
          <ThresholdSelect value={threshold} onChange={onThresholdChange} />
          <WinRateSpan stat={winRates[String(threshold)]} />
        </div>
      </div>
    </div>
  );
}

/** One leaderboard row, expandable into its cached day-by-day detail
 * (fetched on first expand, then kept -- the underlying data is a fixed
 * cache so there's nothing to ever refetch for the same row). */
function MonthlyRowExpandable({
  row,
  isWinner,
  token,
  onAuthError,
  realizedThreshold,
  maxRealizedThreshold,
}: {
  row: MonthlyLeaderboardRow;
  isWinner: boolean;
  token: string;
  onAuthError: (err: unknown) => boolean;
  realizedThreshold: number;
  maxRealizedThreshold: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<MonthlyDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);

  async function toggle() {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (detail || loading) return;
    setLoading(true);
    setError(null);
    try {
      const d = await getMonthlyDetail(token, row.filter_id, row.month);
      setDetail(d);
    } catch (err) {
      if (onAuthError(err)) return;
      setError(err instanceof ApiError ? `${err.status}: ${err.message}` : "failed to load detail");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <tr onClick={toggle} style={{ cursor: "pointer" }}>
        <td className="l">{isWinner ? "🏆" : ""}</td>
        <td className="l">
          {row.strategy_name} <span className="mut">{expanded ? "▲" : "▼"}</span>
        </td>
        <td className="c"><PctSpan v={row.avg_realized} /></td>
        <td className="c"><PctSpan v={row.avg_max_realized} /></td>
        <td className="c"><WinRateSpan stat={row.realized_win_rates[String(realizedThreshold)]} /></td>
        <td className="c"><WinRateSpan stat={row.max_realized_win_rates[String(maxRealizedThreshold)]} /></td>
        <td className="c mut" style={{ fontSize: "0.8rem" }}>
          {row.best_realized_date ?? "—"} <PctSpan v={row.best_realized_value} />
        </td>
        <td className="c mut" style={{ fontSize: "0.8rem" }}>
          {row.worst_realized_date ?? "—"} <PctSpan v={row.worst_realized_value} />
        </td>
        <td className="l mut">{row.top_n} / {row.horizon_days}d</td>
        <td className="l mut">{row.days_run}</td>
        <td className="l">
          {row.is_final ? (
            <span className="mut">final</span>
          ) : (
            <span className="gaps-badge" title="This month hasn't fully resolved yet -- some entry days used a forced early exit. Will update on the next monthly cron run.">
              in progress
            </span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={9} style={{ padding: 0, background: "var(--panel-alt, #f7f8fb)" }}>
            {loading && <div className="mut" style={{ padding: 14 }}>Loading…</div>}
            {error && <div className="error" style={{ margin: 14 }}>{error}</div>}
            {detail && (
              <table className="stocks-table" style={{ margin: "6px 20px 14px" }}>
                <thead>
                  <tr>
                    <th className="l">Entry date</th>
                    <th className="l">Exit date</th>
                    <th className="c">Avg realized</th>
                    <th className="c">Avg max realization</th>
                    <th className="l">Picks</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.days.map((d) => (
                    <DayRow
                      key={d.entry_date}
                      day={d}
                      expanded={expandedDate === d.entry_date}
                      onToggle={() => setExpandedDate(expandedDate === d.entry_date ? null : d.entry_date)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function DayRow({
  day,
  expanded,
  onToggle,
}: {
  day: StrategySimDayResult;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: "pointer" }}>
        <td className="l">
          {day.entry_date}
          {day.forced_early_exit && (
            <span className="gaps-badge" title="This entry day's horizon hasn't fully elapsed yet -- the last available trading day was used as the exit instead.">
              early exit
              <FieldHelp term="strategy_sim_forced_exit" />
            </span>
          )}
        </td>
        <td className="l">{day.exit_date}</td>
        <td className="c"><PctSpan v={day.avg_realized} /></td>
        <td className="c"><PctSpan v={day.avg_max_realized} /></td>
        <td className="l mut">{day.picks.length} stock{day.picks.length === 1 ? "" : "s"} {expanded ? "▲" : "▼"}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} style={{ padding: 0, background: "var(--panel-alt, #f7f8fb)" }}>
            <table className="stocks-table" style={{ margin: "6px 20px" }}>
              <thead>
                <tr>
                  <th className="l">Ticker</th>
                  <th className="c">Predicted signal</th>
                  <th className="c">Realized</th>
                  <th className="c">Max realization</th>
                </tr>
              </thead>
              <tbody>
                {day.picks.map((p) => (
                  <tr key={p.security_id}>
                    <td className="l">{p.display_ticker ?? p.security_id}</td>
                    <td className="c num">{p.predicted_signal === null ? "—" : pct(p.predicted_signal)}</td>
                    <td className="c"><PctSpan v={p.realized_value} /></td>
                    <td className="c"><PctSpan v={p.max_realized_value} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}
